import type { MeshStandardMaterial, Texture } from 'three';

import { TextureLoadPriority, type TextureInfo } from '../../../model/texture-info';
import type { TextureManager } from './texture-manager.class';

// Re-export for convenience
export { TextureLoadPriority } from '../../../model/texture-info';

/**
 * Callback invoked when a texture's material is loaded
 */
type OnMaterialLoadedCallback = (key: string, material: MeshStandardMaterial) => void;

/**
 * A queued texture load request
 */
interface QueuedTextureLoad {
	textureInfo: TextureInfo;
	priority: TextureLoadPriority;
	resolve: (texture: Texture | null) => void;
	reject: (error: Error) => void;
}

/**
 * Pending texture wait request
 */
interface PendingWait {
	resolve: () => void;
	reject: (error: Error) => void;
}

/**
 * TextureLoadingQueue manages prioritized texture loading with concurrency control.
 *
 * Features:
 * - Priority-based queue processing (critical textures load first)
 * - Concurrent load limiting (prevents network saturation)
 * - Promise-based waiting for specific textures
 * - Integrates with existing TextureManager for caching
 *
 * @example
 * ```typescript
 * const queue = new TextureLoadingQueue(textureManager, { basePath: '/assets/' });
 *
 * // Enqueue critical textures
 * await queue.enqueue(floorTextureInfo, TextureLoadPriority.CRITICAL);
 *
 * // Enqueue scene textures (non-blocking)
 * queue.enqueue(countertopInfo, TextureLoadPriority.SCENE);
 *
 * // Wait for specific texture
 * await queue.waitForTexture('granite|fantasybrown');
 * ```
 */
export class TextureLoadingQueue {
	#textureManager: TextureManager | null = null;
	#basePath: string = '';
	#queue: QueuedTextureLoad[] = [];
	#activeLoads = new Map<string, Promise<Texture | null>>();
	#completedKeys = new Set<string>();
	#failedKeys = new Set<string>();
	#pendingWaits = new Map<string, PendingWait[]>();
	#maxConcurrent: number;
	#activeCount = 0;
	#isProcessing = false;
	#onMaterialLoaded: OnMaterialLoadedCallback | null = null;

	constructor(config: { maxConcurrent?: number } = {}) {
		this.#maxConcurrent = config.maxConcurrent ?? 4;
	}

	/**
	 * Configure the queue with a TextureManager and base path.
	 * @param textureManager - The TextureManager to use for loading
	 * @param basePath - Base path for texture files
	 * @param onMaterialLoaded - Optional callback invoked when a material is loaded
	 */
	configure(
		textureManager: TextureManager,
		basePath: string,
		onMaterialLoaded?: OnMaterialLoadedCallback
	): void {
		this.#textureManager = textureManager;
		this.#basePath = basePath;
		this.#onMaterialLoaded = onMaterialLoaded ?? null;
	}

	/**
	 * Check if a texture is already loaded
	 */
	isLoaded(key: string): boolean {
		return this.#completedKeys.has(key);
	}

	/**
	 * Check if a texture failed to load
	 */
	hasFailed(key: string): boolean {
		return this.#failedKeys.has(key);
	}

	/**
	 * Enqueue a texture for loading with the specified priority.
	 * Returns a promise that resolves when the texture is loaded.
	 */
	enqueue(textureInfo: TextureInfo, priority: TextureLoadPriority): Promise<Texture | null> {
		const { key } = textureInfo;

		// If already loaded, resolve immediately
		if (this.#completedKeys.has(key)) {
			return Promise.resolve(this.#textureManager?.getTexture(key) ?? null);
		}

		// If already loading, return the existing promise
		const existingLoad = this.#activeLoads.get(key);
		if (existingLoad) {
			return existingLoad;
		}

		// Create new load promise
		const loadPromise = new Promise<Texture | null>((resolve, reject) => {
			const queuedLoad: QueuedTextureLoad = {
				textureInfo,
				priority,
				resolve,
				reject
			};

			// Insert into queue maintaining priority order (lower priority value = earlier in queue)
			const insertIndex = this.#queue.findIndex((item) => item.priority > priority);
			if (insertIndex === -1) {
				this.#queue.push(queuedLoad);
			} else {
				this.#queue.splice(insertIndex, 0, queuedLoad);
			}
		});

		this.#activeLoads.set(key, loadPromise);

		// Start processing queue
		this.processQueue();

		return loadPromise;
	}

	/**
	 * Wait for a specific texture to be loaded.
	 * If the texture is already loaded, resolves immediately.
	 * If the texture is queued or loading, waits for completion.
	 */
	waitForTexture(key: string): Promise<void> {
		// Already loaded
		if (this.#completedKeys.has(key)) {
			return Promise.resolve();
		}

		// Failed
		if (this.#failedKeys.has(key)) {
			return Promise.reject(new Error(`Texture failed to load: ${key}`));
		}

		// Create pending wait
		return new Promise((resolve, reject) => {
			const waits = this.#pendingWaits.get(key) ?? [];
			waits.push({ resolve, reject });
			this.#pendingWaits.set(key, waits);
		});
	}

	/**
	 * Process the queue, loading textures up to the concurrency limit
	 */
	private processQueue(): void {
		if (this.#isProcessing) return;
		this.#isProcessing = true;

		const processNext = (): void => {
			// If at capacity or queue empty, stop
			if (this.#activeCount >= this.#maxConcurrent || this.#queue.length === 0) {
				this.#isProcessing = false;
				return;
			}

			// Get next item from queue
			const item = this.#queue.shift();
			if (!item) {
				this.#isProcessing = false;
				return;
			}

			// Skip if already completed
			if (this.#completedKeys.has(item.textureInfo.key)) {
				item.resolve(this.#textureManager?.getTexture(item.textureInfo.key) ?? null);
				processNext();
				return;
			}

			// Start loading
			this.#activeCount++;
			this.loadTexture(item.textureInfo)
				.then((texture) => {
					this.#activeCount--;
					this.#activeLoads.delete(item.textureInfo.key);
					this.#completedKeys.add(item.textureInfo.key);
					item.resolve(texture);
					this.resolveWaiters(item.textureInfo.key);
					processNext();
				})
				.catch((error) => {
					this.#activeCount--;
					this.#activeLoads.delete(item.textureInfo.key);
					this.#failedKeys.add(item.textureInfo.key);
					item.reject(error);
					this.rejectWaiters(item.textureInfo.key, error);
					processNext();
				});

			// Continue processing more items
			processNext();
		};

		processNext();
	}

	/**
	 * Load a single texture using TextureManager to ensure materials are created.
	 * This uses the same loading path as loadCriticalTexturesCfn.
	 */
	private async loadTexture(textureInfo: TextureInfo): Promise<Texture | null> {
		if (!this.#textureManager) {
			return null;
		}

		// Use TextureManager.loadTextures() which properly creates materials.
		// Flat-color materials (texture: '') still need their material created and
		// registered so geometry rebuilds find them — TextureManager.createMaterials
		// runs for every TextureInfo regardless of whether a diffuse map was loaded.
		return new Promise<Texture | null>((resolve, reject) => {
			this.#textureManager!.loadTextures([textureInfo], {
				onComplete: () => {
					const material = this.#textureManager!.getMaterial(textureInfo.key);
					if (material) {
						if (this.#onMaterialLoaded) {
							this.#onMaterialLoaded(textureInfo.key, material as MeshStandardMaterial);
						}
						resolve(this.#textureManager!.getTexture(textureInfo.key) ?? null);
					} else {
						reject(new Error(`Material not found after loading: ${textureInfo.key}`));
					}
				},
				onError: (url: string) => {
					console.error('TextureLoadingQueue: Error loading texture:', textureInfo.key, url);
					reject(new Error(`Failed to load texture: ${textureInfo.key} from ${url}`));
				}
			});
		});
	}

	/**
	 * Resolve all pending waiters for a key
	 */
	private resolveWaiters(key: string): void {
		const waits = this.#pendingWaits.get(key);
		if (waits) {
			for (const wait of waits) {
				wait.resolve();
			}
			this.#pendingWaits.delete(key);
		}
	}

	/**
	 * Reject all pending waiters for a key
	 */
	private rejectWaiters(key: string, error: Error): void {
		const waits = this.#pendingWaits.get(key);
		if (waits) {
			for (const wait of waits) {
				wait.reject(error);
			}
			this.#pendingWaits.delete(key);
		}
	}

	/**
	 * Mark a texture as already loaded (e.g., from TextureManager cache)
	 */
	markAsLoaded(key: string): void {
		this.#completedKeys.add(key);
		this.resolveWaiters(key);
	}

	/**
	 * Get the number of pending items in the queue
	 */
	get queueLength(): number {
		return this.#queue.length;
	}

	/**
	 * Get the number of active loads
	 */
	get activeLoadCount(): number {
		return this.#activeCount;
	}

	/**
	 * Cancel all pending loads and clear the queue
	 */
	clear(): void {
		this.#queue = [];
		this.#activeLoads.clear();
		this.#activeCount = 0;
		this.#pendingWaits.clear();
		this.#isProcessing = false;
	}

	/**
	 * Reset the queue (clears completed/failed tracking too)
	 */
	reset(): void {
		this.clear();
		this.#completedKeys.clear();
		this.#failedKeys.clear();
	}
}
